import { useCallback, useRef } from 'react';
import { getVisitData } from '../../services/api';
import { useOASISFormStore } from '../../store/slices/oasisFormSlice';
import { TASK_DETAIL_QIDS } from '../oasis/taskDetailAnswers';
import type { FormValue } from '../oasis/types';

/**
 * Bring the open visit form in step after Task Details saved a value the form
 * also shows (where care was provided, the HCPCS override, the mileage).
 *
 * The form keeps its own copy of every answer and does not re-read the visit
 * once mounted, so the fresh answers are written straight into its store. They
 * come from the server in the form's own answer format. This is a display
 * refresh only: nothing is saved, and the form sends a Task Details answer
 * only when the user changes it there.
 */
export function useSyncTaskDetailAnswers(
  visitId: string | null | undefined
): () => Promise<void> {
  const visitIdRef = useRef(visitId);
  visitIdRef.current = visitId;

  return useCallback(async () => {
    const requestedFor = visitIdRef.current;
    if (!requestedFor) return;
    try {
      const { data } = await getVisitData({
        path: { visit_id: requestedFor },
        throwOnError: true,
      });
      // The panel may have moved to another visit while this was in flight.
      if (visitIdRef.current !== requestedFor) return;
      const answers = (data.visit_data ?? {}) as Record<string, FormValue>;
      const { updateFormField } = useOASISFormStore.getState();
      TASK_DETAIL_QIDS.forEach(qid => {
        updateFormField(qid, answers[qid] ?? '');
      });
    } catch (err) {
      // The save itself succeeded; the form just shows its older copy until
      // the visit is reopened.
      console.error('Failed to refresh task detail answers in the form', err);
    }
  }, []);
}
